import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { inspectorApi } from '@/api';
import { InspectorPublicProfile } from '@/types';
import { Avatar } from '@/ui';

// Public, unauthenticated page at /inspector/:slug.
//
// A shareable "business card" link the inspector puts in their email
// signature, gives to agents, etc. Branded header → optional info panel
// (license, phone) → CTA to /book/:slug. Intentionally minimal: anything
// fancier (photo gallery, reviews, service areas) lives outside Tier 2 —
// see launch-readiness.md #9.

const DEFAULT_BRAND = '#2F6A44';

const sanitizeHexColor = (raw: string | null | undefined): string => {
  if (!raw) return DEFAULT_BRAND;
  const trimmed = raw.trim();
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : DEFAULT_BRAND;
};

const InspectorProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<InspectorPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await inspectorApi.getProfile(slug);
        if (cancelled) return;
        if (resp.success && resp.data) {
          setProfile(resp.data);
        } else {
          setLoadError('This inspector profile is not available.');
        }
      } catch (err) {
        if (cancelled) return;
        const status =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : null;
        setLoadError(
          status === 404
            ? 'This inspector profile is not available.'
            : 'Could not load this inspector profile. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-semibold text-slate-900 mb-2">
            Profile not found
          </h1>
          <p className="text-sm text-slate-600">
            {loadError || 'This inspector profile is not available.'}
          </p>
        </div>
      </div>
    );
  }

  const brand = sanitizeHexColor(profile.brandColor);
  const hasInfo = !!(profile.licenseNumber || profile.phone || profile.serviceArea);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          {/* Branded header. The global `h1` rule in index.css forces
              `text-ink` on headings, so we override with `text-white` on the
              h1 itself rather than relying on cascade from the parent. */}
          <div
            className="px-6 sm:px-8 py-8"
            style={{ backgroundColor: brand }}
          >
            {profile.logoUrl ? (
              <img
                src={profile.logoUrl}
                alt={profile.companyName}
                className="max-h-14 mb-4 bg-white/95 rounded-md p-2 inline-block"
              />
            ) : null}
            {/* Headshot sits next to the name. Avatar falls back to a
                monogram from the inspector name when headshotUrl is null,
                so the layout doesn't collapse for inspectors who haven't
                uploaded a photo. The white ring lifts it off the brand-color
                background. */}
            <div className="flex items-center gap-4">
              <Avatar
                src={profile.headshotUrl}
                name={profile.inspectorName}
                size="2xl"
                tint="neutral"
                // Anchor the crop to the top so headshot photos keep the face
                // in frame; center-crop would shift the face down and out of
                // the circle for typical head-and-shoulders photos.
                objectPosition="top"
                // Suppress the Avatar's default cream inset ring (tuned for
                // white app surfaces) and apply the white ring directly to
                // the Avatar — wrapping in a <div> would stretch wider than
                // the avatar and leave a green sliver between ring and photo.
                bordered={false}
                className="ring-2 ring-white/80"
              />
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-semibold text-white truncate">
                  {profile.inspectorName}
                </h1>
                <p className="text-sm sm:text-base text-white/90 mt-1 truncate">
                  {profile.companyName}
                </p>
              </div>
            </div>
          </div>

          {/* Info panel — only renders when there's at least one fact to show */}
          {hasInfo && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-100">
              {profile.licenseNumber && (
                <div className="bg-white px-6 sm:px-8 py-5">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    License
                  </dt>
                  <dd className="text-sm text-slate-900 mt-1">
                    {profile.licenseNumber}
                  </dd>
                </div>
              )}
              {profile.phone && (
                <div className="bg-white px-6 sm:px-8 py-5">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Phone
                  </dt>
                  <dd className="text-sm text-slate-900 mt-1">
                    <a
                      href={`tel:${profile.phone}`}
                      className="hover:underline"
                      style={{ color: brand }}
                    >
                      {profile.phone}
                    </a>
                  </dd>
                </div>
              )}
              {profile.serviceArea && (
                <div className="bg-white px-6 sm:px-8 py-5 sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Service Area
                  </dt>
                  <dd className="text-sm text-slate-900 mt-1">
                    {profile.serviceArea}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(`/book/${encodeURIComponent(profile.slug)}`)
          }
          className="w-full px-5 py-3 rounded-md text-white text-base font-semibold"
          style={{ backgroundColor: brand }}
        >
          Book an Inspection
        </button>

        <p className="text-xs text-slate-400 text-center mt-8">
          Powered by Flowspect
        </p>
      </div>
    </div>
  );
};

export default InspectorProfilePage;
